/**
 * Backup & Restore API Service
 *
 * API client for backup configuration, history, scheduling,
 * restore operations, and verification endpoints.
 *
 * Uses authFetch pattern consistent with tenantApi.ts.
 *
 * @module services/backupService
 */

import type {
  BackupConfiguration,
  BackupConfigUpdate,
  BackupHistoryListResponse,
  BackupDetailResponse,
  BackupTriggerResponse,
  BackupDeleteResponse,
  BackupToggleResponse,
  BackupPresetsResponse,
  BackupNextRunResponse,
  BackupScopeConfig,
  RestoreInitiatedResponse,
  RestoreHistoryListResponse,
  RestoreDetailResponse,
  VerifyBackupResponse,
} from '../types/backup';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BACKUP_BASE = (tenantId: string) => `/api/tenants/${tenantId}/backups`;

// =============================================================================
// Configuration
// =============================================================================

export const getBackupConfig = async (
  authFetch: AuthFetch,
  tenantId: string,
): Promise<BackupConfiguration | null> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/config`);
  if (response.status === 404) return null;
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch backup config');
  }
  return response.json();
};

export const upsertBackupConfig = async (
  authFetch: AuthFetch,
  tenantId: string,
  config: BackupConfigUpdate,
): Promise<BackupConfiguration> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to save backup config');
  }
  return response.json();
};

// =============================================================================
// Manual Trigger
// =============================================================================

export const triggerBackup = async (
  authFetch: AuthFetch,
  tenantId: string,
  scope?: BackupScopeConfig,
): Promise<BackupTriggerResponse> => {
  const body = scope ? { scope } : {};
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/trigger`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to trigger backup');
  }
  return response.json();
};

// =============================================================================
// History
// =============================================================================

export const listBackupHistory = async (
  authFetch: AuthFetch,
  tenantId: string,
  limit = 20,
  offset = 0,
  filters?: { backup_type?: string; retention_tier?: string },
): Promise<BackupHistoryListResponse> => {
  let url = `${BACKUP_BASE(tenantId)}/?limit=${limit}&offset=${offset}`;
  if (filters?.backup_type) url += `&backup_type=${encodeURIComponent(filters.backup_type)}`;
  if (filters?.retention_tier) url += `&retention_tier=${encodeURIComponent(filters.retention_tier)}`;
  const response = await authFetch(url);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch backup history');
  }
  return response.json();
};

export const getBackupDetail = async (
  authFetch: AuthFetch,
  tenantId: string,
  backupId: string,
): Promise<BackupDetailResponse> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/${backupId}`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch backup detail');
  }
  return response.json();
};

export const deleteBackup = async (
  authFetch: AuthFetch,
  tenantId: string,
  backupId: string,
): Promise<BackupDeleteResponse> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/${backupId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to delete backup');
  }
  return response.json();
};

// =============================================================================
// Scheduling
// =============================================================================

export const toggleBackupSchedule = async (
  authFetch: AuthFetch,
  tenantId: string,
  enabled: boolean,
): Promise<BackupToggleResponse> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/config/toggle`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to toggle backup schedule');
  }
  return response.json();
};

export const getBackupPresets = async (
  authFetch: AuthFetch,
  tenantId: string,
): Promise<BackupPresetsResponse> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/config/presets`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch backup presets');
  }
  return response.json();
};

export const getNextRun = async (
  authFetch: AuthFetch,
  tenantId: string,
): Promise<BackupNextRunResponse> => {
  const response = await authFetch(`${BACKUP_BASE(tenantId)}/next-run`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch next run info');
  }
  return response.json();
};

// =============================================================================
// Restore
// =============================================================================

export const initiateRestore = async (
  authFetch: AuthFetch,
  tenantId: string,
  backupId: string,
  skipVerification = false,
): Promise<RestoreInitiatedResponse> => {
  const response = await authFetch(
    `/api/tenants/${tenantId}/backups/${backupId}/restore`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skip_verification: skipVerification }),
    },
  );
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to initiate restore');
  }
  return response.json();
};

export const listRestoreHistory = async (
  authFetch: AuthFetch,
  tenantId: string,
  limit = 20,
  offset = 0,
): Promise<RestoreHistoryListResponse> => {
  const response = await authFetch(
    `/api/tenants/${tenantId}/restores?limit=${limit}&offset=${offset}`,
  );
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch restore history');
  }
  return response.json();
};

export const getRestoreStatus = async (
  authFetch: AuthFetch,
  tenantId: string,
  restoreId: string,
): Promise<RestoreDetailResponse> => {
  const response = await authFetch(
    `/api/tenants/${tenantId}/restores/${restoreId}`,
  );
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch restore status');
  }
  return response.json();
};

export const verifyBackup = async (
  authFetch: AuthFetch,
  tenantId: string,
  backupId: string,
): Promise<VerifyBackupResponse> => {
  const response = await authFetch(
    `/api/tenants/${tenantId}/backups/${backupId}/verify`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to verify backup');
  }
  return response.json();
};
